import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { lumi } from '@/lib/lumi'

import {Trophy, Medal, Rocket, ArrowLeft, Wallet, Star} from 'lucide-react'

interface LeaderboardEntry {
  _id: string
  userId: string
  username: string
  score: number
  difficulty: number
  createdAt: string
}

function Leaderboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadLeaderboard()
  }, [])

  // Refresh on navigation (when component mounts or remounts)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLeaderboard()
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [location.pathname])

  // Refresh leaderboard when page becomes visible or window gains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadLeaderboard()
      }
    }

    const handleFocus = () => {
      loadLeaderboard()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const loadLeaderboard = async () => {
    setLoading(true)
    try {
      const timestamp = Date.now()
      console.log(`[Leaderboard ${timestamp}] 🔄 Loading leaderboard...`)
      
      const { list } = await lumi.entities.scores.list({
        sort: { createdAt: -1 },
        limit: 10000
      })
      
      console.log(`[Leaderboard ${timestamp}] 📊 Loaded ${list.length} total scores`)
      
      // Filter out dummy data on frontend (delete requires admin permissions)
      const realScores = list.filter((entry: LeaderboardEntry) => 
        !['user_demo_001', 'user_demo_002', 'user_demo_003'].includes(entry.userId)
      )
      
      console.log(`[Leaderboard ${timestamp}] ✅ Real scores after filtering: ${realScores.length}`)
      
      // Sum up all moons collected by each user (lifetime total)
      const userTotals = new Map<string, LeaderboardEntry>()
      realScores.forEach((entry: LeaderboardEntry) => {
        const existing = userTotals.get(entry.userId)
        if (existing) {
          existing.score += Number(entry.score) || 0
          // Keep the most recent date
          if (new Date(entry.createdAt) > new Date(existing.createdAt)) {
            existing.createdAt = entry.createdAt
          }
        } else {
          userTotals.set(entry.userId, { ...entry })
        }
      })
      
      const sortedLeaderboard = Array.from(userTotals.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, 10)
      
      console.log(`[Leaderboard ${timestamp}] 🏆 Final leaderboard:`, sortedLeaderboard.map(e => ({ user: e.username, moons: e.score })))
      setLeaderboard(sortedLeaderboard)
    } catch (error) {
      console.error('[Leaderboard] ❌ Failed to load leaderboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMedalIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="text-yellow-400" size={28} />
    if (rank === 2) return <Medal className="text-gray-300" size={28} />
    if (rank === 3) return <Medal className="text-orange-500" size={28} />
    return <span className="text-gray-400 font-bold text-lg">#{rank}</span>
  }

  const getRankClass = (rank: number) => {
    if (rank === 1) return 'bg-yellow-500/20 border-yellow-400'
    if (rank === 2) return 'bg-gray-300/20 border-gray-300'
    if (rank === 3) return 'bg-orange-500/20 border-orange-500'
    return 'bg-black/40 border-purple-500/30'
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0118] via-[#0f0520] to-[#1a0a2e] p-4 overflow-hidden relative">
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(200)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 0.5 + 'px',
              height: Math.random() * 3 + 0.5 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: Math.random() * 3 + 's',
              animationDuration: Math.random() * 2 + 2 + 's',
              opacity: Math.random() * 0.7 + 0.3,
              boxShadow: `0 0 ${Math.random() * 4 + 2}px rgba(255,255,255,${Math.random() * 0.6})`
            }}
          />
        ))}
      </div>

      {/* Nebula glow effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute w-96 h-96 rounded-full blur-3xl bg-purple-500/40 left-10 top-20 animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute w-80 h-80 rounded-full blur-3xl bg-blue-500/30 right-20 top-60 animate-pulse" style={{ animationDuration: '10s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto pt-8 pb-20">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 hover:gap-3 group"
        >
          <ArrowLeft size={20} className="group-hover:animate-pulse" />
          <span className="font-semibold">Menu</span>
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <Star className="text-yellow-400 animate-pulse" size={40} />
            <h1 className="text-4xl md:text-5xl font-black text-white" style={{ 
              textShadow: '0 0 30px rgba(251,191,36,0.8), 0 0 60px rgba(220,38,38,0.5)' 
            }}>
              GLOBAL LEADERBOARD
            </h1>
            <Star className="text-yellow-400 animate-pulse" size={40} />
          </div>
          <p className="text-gray-300 text-lg font-semibold">Top Rocket Bunny Champions</p>
        </div>

        {/* Leaderboard */}
        <div className="bg-black/60 backdrop-blur-xl p-6 md:p-8 rounded-2xl border-2 border-purple-500/50 shadow-[0_0_60px_rgba(168,85,247,0.3)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mb-4"></div>
              <div className="text-white text-xl font-semibold">Loading champions...</div>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Trophy size={80} className="mx-auto mb-4 text-gray-500 animate-pulse" />
              <p className="text-gray-400 text-xl font-semibold mb-2">No scores yet!</p>
              <p className="text-gray-500 mb-6">Be the first champion to conquer space!</p>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl shadow-lg hover:shadow-red-600/50 transition-all duration-300 hover:scale-105"
              >
                🚀 Start Your Journey
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry, index) => {
                const rank = index + 1
                return (
                  <div
                    key={entry._id}
                    className={`p-3 md:p-4 rounded-lg border ${getRankClass(rank)} transition-all duration-200`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="flex-shrink-0 w-14 flex items-center justify-center">
                        {getMedalIcon(rank)}
                      </div>

                      {/* Player Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg md:text-xl font-bold text-white break-words">
                          {entry.username}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-400">
                          {new Date(entry.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>

                      {/* Score */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl md:text-3xl font-bold text-white">
                          {entry.score.toLocaleString()}
                        </div>
                        <div className="text-xs md:text-sm text-gray-400 flex items-center gap-1 justify-end">
                          <Rocket size={12} />
                          {entry.difficulty.toFixed(1)}x
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* CTA */}
        {!loading && leaderboard.length > 0 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/login')}
              className="px-10 py-4 bg-gradient-to-r from-red-600 via-red-700 to-red-600 hover:from-red-700 hover:via-red-800 hover:to-red-700 text-white text-xl font-black rounded-xl shadow-[0_0_40px_rgba(220,38,38,0.5)] hover:shadow-[0_0_60px_rgba(220,38,38,0.8)] transition-all duration-300 hover:scale-105 border-2 border-red-400"
            >
              🚀 JOIN THE COMPETITION
            </button>
          </div>
        )}
      </div>

    </div>
  )
}

export default Leaderboard
